/**
 * 票链云 — 接口清单（与 @ilc/shared 的 EndpointMap 一一对应）
 *
 * 后端正在并行开发，本文件按契约先写好全部函数：接口一旦就绪，页面无需改动。
 * 所有函数失败时抛 ApiError，页面用 useResource 统一降级为空状态 + 错误提示。
 */

import type {
  ArchiveListItem,
  AuditLogQuery,
  AuthContext,
  CheckManualOverrideRequest,
  CheckReport,
  CheckRetryRequest,
  BizAlert,
  BizAlertHandleRequest,
  BizAlertListQuery,
  Contract,
  ContractOption,
  ContractAttachment,
  ContractChangeApplyRequest,
  ContractChangeCreateRequest,
  ContractDetailResponse,
  ContractEvaluationCreateRequest,
  ContractItemInput,
  ContractPaymentConfirmRequest,
  ContractPaymentCreateRequest,
  ContractStageInput,
  Dept,
  DeptCreateRequest,
  ExpenseApply,
  ExpenseApplyCreateRequest,
  ExpenseApplyListQuery,
  ExpenseBudget,
  ExpenseBudgetAdjust,
  ExpenseBudgetAdjustRequest,
  ExpenseBudgetCreateRequest,
  ExpenseBudgetExecution,
  ExpenseBudgetListQuery,
  ExpensePaymentCreateRequest,
  ExpenseReimburse,
  ExpenseReimburseCreateRequest,
  ExpenseReimburseListQuery,
  ExpenseReimburseUpdateRequest,
  ExpenseReportQuery,
  ExpenseReportResponse,
  ExpenseItemInput,
  FlowInstance,
  FlowTask,
  FlowTaskHandleRequest,
  FlowTaskListQuery,
  FlowTemplate,
  FlowTemplateCreateRequest,
  InvoiceAttachment,
  InvoiceBatchUploadResponse,
  InvoiceDeleteRequest,
  InvoiceDeleteResult,
  InvoiceDetail,
  InvoiceListItem,
  InvoiceListQuery,
  InvoiceOcrUpdateRequest,
  InvoiceRejectRequest,
  InvoiceSubmitRequest,
  InvoiceUploadResponse,
  LoginRequest,
  LoginResponse,
  Message,
  MessageListQuery,
  MetadataResponse,
  OperationLog,
  OperationLogQuery,
  PageQuery,
  PageResult,
  ProjectListItem,
  PlatformMetrics,
  Plan,
  ProviderCallLog,
  ProviderCallLogQuery,
  ProviderConfig,
  ProviderConfigUpdateRequest,
  QuotaAdjustRequest,
  Role,
  RoleCreateRequest,
  RoleUpdateRequest,
  Tenant,
  TenantChangePlanRequest,
  TenantConfig,
  TenantConfigUpdateRequest,
  TenantListItem,
  TenantListQuery,
  TenantMetrics,
  TenantRegisterRequest,
  TenantRegisterResponse,
  TenantSuspendRequest,
  TenantVerifyRequest,
  TplusPushRequest,
  TplusPushResponse,
  TplusSync,
  UnreadCountResponse,
  User,
  UserCreateRequest,
  UserListQuery,
  UserUpdateRequest,
} from '@ilc/shared';
import { api, cleanQuery } from './client';

/* ==========================================================================
 * 认证
 * ========================================================================== */

export const authApi = {
  /** 租户用户填 tenantCode；平台运营人员留空 */
  login(body: LoginRequest): Promise<LoginResponse> {
    return api.post<LoginResponse>('/auth/login', body);
  },
  register(body: TenantRegisterRequest): Promise<TenantRegisterResponse> {
    return api.post<TenantRegisterResponse>('/auth/register', body);
  },
  refresh(refreshToken: string): Promise<LoginResponse> {
    return api.post<LoginResponse>('/auth/refresh', { refreshToken }, { silent: true });
  },
  me(): Promise<AuthContext> {
    return api.get<AuthContext>('/auth/me', undefined, { silent: true });
  },
  logout(): Promise<{ ok: boolean }> {
    return api.post<{ ok: boolean }>('/auth/logout', undefined, { silent: true });
  },
  /**
   * 修改**自己的**密码。
   *
   * 必须传当前密码：仅有登录态不足以证明操作者是本人，
   * 否则令牌被盗后攻击者可以直接改密码，把账号主人锁在外面。
   */
  changePassword(body: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ ok: boolean }> {
    return api.post<{ ok: boolean }>('/auth/change-password', body);
  },
};

/* ==========================================================================
 * 元数据与工作台
 * ========================================================================== */

export const metaApi = {
  metadata(): Promise<MetadataResponse> {
    return api.get<MetadataResponse>('/meta', undefined, { silent: true });
  },
  tenantMetrics(): Promise<TenantMetrics> {
    return api.get<TenantMetrics>('/metrics', undefined, { silent: true });
  },
};

/* ==========================================================================
 * 发票
 * ========================================================================== */

export const invoiceApi = {
  list(query: InvoiceListQuery): Promise<PageResult<InvoiceListItem>> {
    return api.get<PageResult<InvoiceListItem>>('/invoices', cleanQuery(query));
  },
  /**
   * 取当前筛选条件下的**全部**发票（分页拉取），供「导出专用报表」用。
   *
   * 【为什么不能只导当前页】报表是交给财务/审计的成品，应该覆盖筛选出来的
   * 全部发票，而不是屏幕上碰巧那一页——翻页才能导全，必然漏。
   *
   * 【为什么要有上限】不设上限的话，一个没有筛选条件的导出会把整个租户的发票
   * 一次拉进浏览器内存。超过上限时**如实返回 truncated=true**，由界面提示用户
   * 缩小范围，而不是默默截断（截断的报表比报错危险得多：数字看着是"全量"）。
   */
  async listAll(
    query: InvoiceListQuery,
    options: { cap?: number; pageSize?: number } = {},
  ): Promise<{ rows: InvoiceListItem[]; truncated: boolean; total: number }> {
    const cap = options.cap ?? 2000;
    const pageSize = Math.min(options.pageSize ?? 200, 200);
    const rows: InvoiceListItem[] = [];
    let page = 1;
    let total = 0;

    for (;;) {
      const res = await api.get<PageResult<InvoiceListItem>>(
        '/invoices',
        cleanQuery({ ...query, page, pageSize }),
      );
      total = res.total;
      rows.push(...res.list);
      if (rows.length >= total || res.list.length === 0) break;
      if (rows.length >= cap) return { rows: rows.slice(0, cap), truncated: true, total };
      page += 1;
    }
    return { rows, truncated: rows.length < total, total };
  },
  detail(id: string): Promise<InvoiceDetail> {
    return api.get<InvoiceDetail>(`/invoices/${id}`);
  },
  /** 单张上传（multipart） */
  upload(
    file: File,
    meta: {
      contractNo?: string;
      bizNo?: string;
      invoiceType?: string;
      remark?: string;
    },
    onProgress?: (percent: number) => void,
  ): Promise<InvoiceUploadResponse> {
    const form = new FormData();
    form.append('file', file);
    if (meta.contractNo) form.append('contractNo', meta.contractNo);
    if (meta.bizNo) form.append('bizNo', meta.bizNo);
    if (meta.invoiceType) form.append('invoiceType', meta.invoiceType);
    if (meta.remark) form.append('remark', meta.remark);
    return api.upload<InvoiceUploadResponse>('/invoices', form, { onProgress });
  },
  /**
   * 批量上传 —— 逐文件调用，单个失败不中断整批。
   * 契约里没有独立的批量端点，前端按文件并发调用同一端点后聚合结果。
   */
  async batchUpload(
    files: File[],
    meta: { contractNo?: string; remark?: string } = {},
    onEach?: (fileName: string, done: number, total: number) => void,
  ): Promise<InvoiceBatchUploadResponse> {
    const succeeded: InvoiceUploadResponse[] = [];
    const failed: Array<{ fileName: string; reason: string }> = [];
    let done = 0;
    for (const file of files) {
      try {
        succeeded.push(await invoiceApi.upload(file, meta));
      } catch (err) {
        failed.push({
          fileName: file.name,
          reason: err instanceof Error ? err.message : '上传失败',
        });
      }
      done += 1;
      onEach?.(file.name, done, files.length);
    }
    return { succeeded, failed };
  },
  /** 人工修正 OCR 识别结果（留痕） */
  updateOcr(id: string, body: InvoiceOcrUpdateRequest): Promise<InvoiceDetail> {
    return api.patch<InvoiceDetail>(`/invoices/${id}/ocr`, body);
  },
  submit(id: string, body: InvoiceSubmitRequest = {}): Promise<InvoiceDetail> {
    return api.post<InvoiceDetail>(`/invoices/${id}/submit`, body);
  },
  /** 退回经办人 —— 原因必填 */
  reject(id: string, body: InvoiceRejectRequest): Promise<InvoiceDetail> {
    return api.post<InvoiceDetail>(`/invoices/${id}/reject`, body);
  },
  report(id: string): Promise<CheckReport> {
    return api.get<CheckReport>(`/invoices/${id}/report`);
  },
  retryChecks(id: string, body: CheckRetryRequest = {}): Promise<InvoiceDetail> {
    return api.post<InvoiceDetail>(`/invoices/${id}/checks/retry`, body);
  },
  /** 外部校验不可用时的人工接管（文档七） */
  manualOverride(id: string, body: CheckManualOverrideRequest): Promise<InvoiceDetail> {
    return api.post<InvoiceDetail>(`/invoices/${id}/override`, body);
  },
  /** 推送 T+ —— 高风险票会被后端拦截并返回 blockReasons */
  pushTplus(id: string, body: TplusPushRequest = {}): Promise<TplusPushResponse> {
    return api.post<TplusPushResponse>(`/invoices/${id}/push-tplus`, body);
  },
  /**
   * 删除一张未过审的发票（连同复印件附件与原件文件）。
   *
   * `reason` 必填，服务端写进操作日志——删除不可逆，事后只剩这条记录可复核。
   * 返回值里的 `keptFiles` 要如实展示：文件按内容哈希去重，被别的记录共用时
   * 服务端会保留它，此时"原件已一并删除"并不成立。
   */
  remove(id: string, body: InvoiceDeleteRequest): Promise<InvoiceDeleteResult> {
    return api.delete<InvoiceDeleteResult>(`/invoices/${id}`, { data: body });
  },
  /**
   * 补充 / 更换发票的合同关联。传 `null` 表示解除关联。
   *
   * 【不要改回 invoiceApi.submit】从前的「关联合同」借道提交接口，
   * 而提交接口只放行 DRAFT / REJECTED，于是**发票一旦进过流程就再也关联不上合同**
   * （报「不能重复提交」）——而这正是财务初审时最想补的一步。
   * 后端已给出独立端点 PATCH /invoices/:id/contract。
   */
  setContract(
    id: string,
    contractNo: string | null,
  ): Promise<{ contractId: string | null; contractNo: string | null }> {
    return api.patch<{ contractId: string | null; contractNo: string | null }>(
      `/invoices/${id}/contract`,
      { contractNo },
    );
  },
  /**
   * 设置发票的工程归属。传 `null` 表示解除关联。
   *
   * 后端会**同名复用**已有工程（字典表唯一约束），因此这里不需要
   * "先建工程再关联"两步——直接传名字即可。
   */
  setProject(
    id: string,
    name: string | null,
  ): Promise<{ projectId: string | null; projectName: string | null }> {
    // 后端是 PATCH /invoices/:id/project
    return api.patch<{ projectId: string | null; projectName: string | null }>(
      `/invoices/${id}/project`,
      { name },
    );
  },

  /* —— 发票复印件附件 —— */
  /**
   * 上传发票复印件并触发手写内容提取。
   *
   * 注意**提取失败不会让上传失败**——原件已保存，失败原因在返回体的
   * `handwritingStatus` / `handwritingMessage` 里，界面必须如实展示。
   * 尤其不能把 `UNAVAILABLE`（通道未启用）渲染成"没有手写内容"：
   * 那是"没做"，不是"做了没找到"。
   */
  uploadAttachment(
    invoiceId: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<InvoiceAttachment> {
    const form = new FormData();
    form.append('file', file);
    return api.upload<InvoiceAttachment>(`/invoices/${invoiceId}/attachments`, form, { onProgress });
  },
  /** 附件列表。**不含手写全文与逐行明细**——要那些得再调 attachmentDetail */
  listAttachments(invoiceId: string): Promise<InvoiceAttachment[]> {
    return api.get<InvoiceAttachment[]>(`/invoices/${invoiceId}/attachments`);
  },
  attachmentDetail(attachmentId: string): Promise<InvoiceAttachment> {
    return api.get<InvoiceAttachment>(`/invoices/attachments/${attachmentId}`);
  },
  /** 人工修正手写内容。原因至少 8 个字，前后端都会拦 */
  updateHandwriting(
    attachmentId: string,
    body: { text: string | null; reason: string },
  ): Promise<InvoiceAttachment> {
    return api.patch<InvoiceAttachment>(`/invoices/attachments/${attachmentId}/handwriting`, body);
  },
  reparseAttachment(attachmentId: string): Promise<InvoiceAttachment> {
    return api.post<InvoiceAttachment>(`/invoices/attachments/${attachmentId}/reparse`, {});
  },
  attachmentUrl(attachmentId: string): Promise<{ url: string }> {
    return api.post<{ url: string }>(`/invoices/attachments/${attachmentId}/url`, {});
  },
  removeAttachment(attachmentId: string): Promise<{ ok: boolean }> {
    return api.delete<{ ok: boolean }>(`/invoices/attachments/${attachmentId}`);
  },
};

/* ==========================================================================
 * 流程
 * ========================================================================== */

export const flowApi = {
  tasks(query: FlowTaskListQuery): Promise<PageResult<FlowTask>> {
    return api.get<PageResult<FlowTask>>('/flow/tasks', cleanQuery(query));
  },
  handle(taskId: string, body: FlowTaskHandleRequest): Promise<FlowInstance> {
    return api.post<FlowInstance>(`/flow/tasks/${taskId}/handle`, body);
  },
  templates(): Promise<FlowTemplate[]> {
    return api.get<FlowTemplate[]>('/flow/templates');
  },
  createTemplate(body: FlowTemplateCreateRequest): Promise<FlowTemplate> {
    return api.post<FlowTemplate>('/flow/templates', body);
  },
  updateTemplate(id: string, body: Partial<FlowTemplateCreateRequest>): Promise<FlowTemplate> {
    return api.put<FlowTemplate>(`/flow/templates/${id}`, body);
  },
};

/* ==========================================================================
 * 组织与权限
 * ========================================================================== */

export const orgApi = {
  users(query: UserListQuery): Promise<PageResult<User>> {
    return api.get<PageResult<User>>('/users', cleanQuery(query));
  },
  createUser(body: UserCreateRequest): Promise<User> {
    return api.post<User>('/users', body);
  },
  updateUser(id: string, body: UserUpdateRequest): Promise<User> {
    // 同 updateRole：后端是 `iamRouter.put('/users/:id')`，写 PATCH 会 404
    return api.put<User>(`/users/${id}`, body);
  },
  roles(): Promise<Role[]> {
    return api.get<Role[]>('/roles');
  },
  createRole(body: RoleCreateRequest): Promise<Role> {
    return api.post<Role>('/roles', body);
  },
  updateRole(body: RoleUpdateRequest): Promise<Role> {
    const { id, ...rest } = body;
    /**
     * 【必须是 PUT，不能是 PATCH】
     * 后端 `iam.router.ts` 注册的是 `iamRouter.put('/roles/:id')`，没有 PATCH 版本。
     * 从前这里写成 PATCH，请求打过去是 404「接口不存在」，而 `performAction` 把
     * 404 一律当成"后端接口尚未就绪"，于是弹一句「本次保存仅在本地演示，未写入数据库」
     * 就过去了——**看起来像环境问题，实则是个动词写错的 bug，而且改了不生效**。
     *
     * 这个项目里 PUT 用于资源整体更新（`/roles/:id`、`/users/:id`、`/depts/:id`），
     * PATCH 用于子资源（`/invoices/:id/ocr`、`/messages/:id/read`）。
     * 后端接的是 `roleBodySchema.partial()`，所以传部分字段也没问题。
     */
    return api.put<Role>(`/roles/${id}`, rest);
  },
  depts(): Promise<Dept[]> {
    return api.get<Dept[]>('/depts');
  },
  createDept(body: DeptCreateRequest): Promise<Dept> {
    return api.post<Dept>('/depts', body);
  },
};

/* ==========================================================================
 * 合同与档案
 * ========================================================================== */

/** 工程名称字典（下拉候选） */
export const projectApi = {
  list(keyword?: string): Promise<ProjectListItem[]> {
    return api.get<ProjectListItem[]>('/projects', cleanQuery({ keyword }));
  },
};

export const contractApi = {
  list(query: PageQuery & { keyword?: string } = {}): Promise<PageResult<Contract>> {
    return api.get<PageResult<Contract>>('/contracts', cleanQuery(query));
  },
  /**
   * 合同下拉候选（只回 id / 合同号 / 名称）。
   *
   * 【为什么不用 list】费用申请与报销的「关联合同」是可选下拉，使用者包括
   * 「部门负责人」「普通员工」——他们没有 contract:read（合同金额与条款是
   * 商务敏感信息）。从前这两个页面直接调 list 取 200 条，页面上会弹
   * 「没有执行该操作的权限（需要：contract:read）」，而字段也用不了。
   */
  options(keyword?: string): Promise<ContractOption[]> {
    return api.get<ContractOption[]>('/contracts/options', cleanQuery({ keyword }));
  },
  /**
   * 合同详情。
   *
   * 【返回形状在 2026-09-23 变了】从前只返回合同本身，现在返回
   * `{ contract, items, stages, payments, changes, evaluations, children,
   *    alerts, approvals, invoices, attachments }`——把子表一次取回。
   * 详情页要同时看到这些，拆成六个接口意味着六个加载态与六处"面板为什么是空的"。
   * **列表接口不返回这些**（避免 N+1 与响应膨胀）。
   */
  detail(id: string): Promise<ContractDetailResponse> {
    return api.get<ContractDetailResponse>(`/contracts/${id}`);
  },
  create(body: Partial<Contract> & { attachmentIds?: string[] }): Promise<Contract> {
    return api.post<Contract>('/contracts', body);
  },
  update(id: string, body: Partial<Contract> & { attachmentIds?: string[] }): Promise<Contract> {
    return api.patch<Contract>(`/contracts/${id}`, body);
  },

  /* —— 合同附件 —— */
  /**
   * 上传附件并触发解析。
   *
   * `contractId` 可选：不传时是 TEMP 态（「从文件新建合同」的先上传后建合同流程）。
   * 注意**解析失败不会让上传失败**——原件已保存，失败原因在返回体的
   * `extractStatus` / `extractMessage` 里，界面必须如实展示。
   */
  uploadAttachment(
    file: File,
    contractId?: string,
    onProgress?: (percent: number) => void,
  ): Promise<ContractAttachment> {
    const form = new FormData();
    form.append('file', file);
    if (contractId) form.append('contractId', contractId);
    return api.upload<ContractAttachment>('/contracts/attachments', form, { onProgress });
  },
  attachmentDetail(id: string): Promise<ContractAttachment> {
    return api.get<ContractAttachment>(`/contracts/attachments/${id}`);
  },
  reparseAttachment(attachmentId: string): Promise<ContractAttachment> {
    return api.post<ContractAttachment>('/contracts/attachments/reparse', { attachmentId });
  },
  /** 换取带签名的原件下载链接（这就是前端第一次真正用上 /api/files 取件能力） */
  attachmentUrl(id: string): Promise<{ url: string }> {
    return api.post<{ url: string }>(`/contracts/attachments/${id}/url`, {});
  },
  removeAttachment(id: string): Promise<{ ok: boolean }> {
    return api.delete<{ ok: boolean }>(`/contracts/attachments/${id}`);
  },

  /* ==========================================================================
   * 合同全生命周期（延伸需求第一部分）
   * ========================================================================== */

  /**
   * 提交审批。
   *
   * 【为什么是独立端点】提交有它自己的前置条件（只有草稿/被退回可提交），
   * 与"改合同"是两件事。借道编辑接口的后果是：
   * 一旦合同进过流程，就再也提交不了第二次（发票那边踩过这个坑）。
   */
  submit(id: string): Promise<ContractDetailResponse> {
    return api.post<ContractDetailResponse>(`/contracts/${id}/submit`, {});
  },
  /** 归档：**执行人在项目结束时主动做**，不是流程终点。见后端 service 的说明 */
  archive(id: string): Promise<ContractDetailResponse> {
    return api.post<ContractDetailResponse>(`/contracts/${id}/archive`, {});
  },
  /** 明细 / 结算阶段：整体替换语义（传全量，不传则该维度不动） */
  saveItems(id: string, items: ContractItemInput[]): Promise<ContractDetailResponse> {
    return api.post<ContractDetailResponse>(`/contracts/${id}/items`, { items });
  },
  saveStages(id: string, stages: ContractStageInput[]): Promise<ContractDetailResponse> {
    return api.post<ContractDetailResponse>(`/contracts/${id}/stages`, { stages });
  },
  /* —— 变更 / 补充协议 —— */
  createChange(id: string, body: ContractChangeCreateRequest): Promise<{ id: string; changeNo: string }> {
    return api.post<{ id: string; changeNo: string }>(`/contracts/${id}/changes`, body);
  },
  applyChange(id: string, changeId: string, body: ContractChangeApplyRequest = {}): Promise<ContractDetailResponse> {
    return api.post<ContractDetailResponse>(`/contracts/${id}/changes/${changeId}/apply`, body);
  },
  /* —— 付款 —— */
  createPayment(id: string, body: ContractPaymentCreateRequest): Promise<{ id: string; paymentNo: string }> {
    return api.post<{ id: string; paymentNo: string }>(`/contracts/${id}/payments`, body);
  },
  confirmPayment(
    id: string,
    paymentId: string,
    body: ContractPaymentConfirmRequest,
  ): Promise<ContractDetailResponse & { overContractCents: number; warning: string | null }> {
    return api.post(`/contracts/${id}/payments/${paymentId}/confirm`, body);
  },
  /* —— 履约评价 —— */
  createEvaluation(id: string, body: ContractEvaluationCreateRequest): Promise<{ id: string }> {
    return api.post<{ id: string }>(`/contracts/${id}/evaluations`, body);
  },
  /* —— 预警 —— */
  alerts(query: BizAlertListQuery = {}): Promise<PageResult<BizAlert>> {
    return api.get<PageResult<BizAlert>>('/contracts/alerts', cleanQuery(query));
  },
  handleAlert(id: string, body: BizAlertHandleRequest): Promise<BizAlert> {
    return api.patch<BizAlert>(`/contracts/alerts/${id}`, body);
  },
  /**
   * 导出合同台账。
   *
   * 后端返回的是 **CSV 流**（`GET`），不是 `{url}`——那一条是档案导出的形态，
   * 两者不要混淆（`utils/export.ts` 的注释里也写了）。
   * 这里直接打开新窗口让浏览器下载，导出动作本身在后端已写审计。
   */
  exportUrl(query: Record<string, unknown> = {}): string {
    const qs = new URLSearchParams(
      Object.entries(cleanQuery(query)).map(([k, v]) => [k, String(v)]),
    ).toString();
    return `/api/contracts/export${qs ? `?${qs}` : ''}`;
  },
};

/* ==========================================================================
 * 费用报销（延伸需求第二部分）
 * ========================================================================== */

export const expenseApi = {
  /* —— 费用申请（事前） —— */
  listApplies(query: ExpenseApplyListQuery = {}): Promise<PageResult<ExpenseApply>> {
    return api.get<PageResult<ExpenseApply>>('/expenses/applies', cleanQuery(query));
  },
  applyDetail(id: string): Promise<ExpenseApply> {
    return api.get<ExpenseApply>(`/expenses/applies/${id}`);
  },
  createApply(body: ExpenseApplyCreateRequest): Promise<{ id: string; applyNo: string; budgetWarning: string | null }> {
    return api.post(`/expenses/applies`, body);
  },
  updateApply(id: string, body: Partial<ExpenseApplyCreateRequest>): Promise<ExpenseApply> {
    return api.patch<ExpenseApply>(`/expenses/applies/${id}`, body);
  },
  removeApply(id: string): Promise<{ ok: boolean }> {
    return api.delete<{ ok: boolean }>(`/expenses/applies/${id}`);
  },
  submitApply(id: string): Promise<ExpenseApply & { flowInstanceId: string }> {
    return api.post(`/expenses/applies/${id}/submit`, {});
  },
  cancelApply(id: string, reason: string): Promise<ExpenseApply> {
    return api.post<ExpenseApply>(`/expenses/applies/${id}/cancel`, { reason });
  },

  /* —— 报销单（事后） —— */
  listReimburse(query: ExpenseReimburseListQuery = {}): Promise<PageResult<ExpenseReimburse>> {
    return api.get<PageResult<ExpenseReimburse>>('/expenses/reimburse', cleanQuery(query));
  },
  reimburseDetail(id: string): Promise<ExpenseReimburse> {
    return api.get<ExpenseReimburse>(`/expenses/reimburse/${id}`);
  },
  createReimburse(body: ExpenseReimburseCreateRequest): Promise<{ id: string; reimburseNo: string }> {
    return api.post(`/expenses/reimburse`, body);
  },
  updateReimburse(id: string, body: ExpenseReimburseUpdateRequest): Promise<ExpenseReimburse> {
    return api.patch<ExpenseReimburse>(`/expenses/reimburse/${id}`, body);
  },
  removeReimburse(id: string): Promise<{ ok: boolean; releasedCount: number }> {
    return api.delete<{ ok: boolean; releasedCount: number }>(`/expenses/reimburse/${id}`);
  },
  submitReimburse(id: string): Promise<ExpenseReimburse & { flowInstanceId: string; budgetWarning: string | null }> {
    return api.post(`/expenses/reimburse/${id}/submit`, {});
  },

  /**
   * 上传并校验发票。
   *
   * 这一步会**同步跑完 OCR → 企查查 → 验真 → 报销专属四项校验**，
   * 所以比普通上传慢（外部通道要时间）。界面上要给"正在校验"的状态，
   * 否则用户以为卡住了会重复点。校验失败不会让上传失败——
   * 失败原因会作为风险项出现在返回体里。
   */
  uploadInvoice(
    id: string,
    file: File,
    itemId?: string | null,
    onProgress?: (percent: number) => void,
  ): Promise<ExpenseReimburse> {
    const form = new FormData();
    form.append('file', file);
    if (itemId) form.append('itemId', itemId);
    return api.upload<ExpenseReimburse>(`/expenses/reimburse/${id}/invoices`, form, { onProgress });
  },
  /** 释放发票（置 released_at 而非删行，留痕且可重新使用） */
  releaseInvoice(id: string, linkId: string, reason: string): Promise<ExpenseReimburse> {
    return api.delete<ExpenseReimburse>(`/expenses/reimburse/${id}/invoices/${linkId}`, {
      data: { reason },
    });
  },
  pay(id: string, body: ExpensePaymentCreateRequest): Promise<ExpenseReimburse> {
    return api.post<ExpenseReimburse>(`/expenses/reimburse/${id}/payments`, body);
  },

  /* —— 预算 —— */
  listBudgets(query: ExpenseBudgetListQuery = {}): Promise<PageResult<ExpenseBudget>> {
    return api.get<PageResult<ExpenseBudget>>('/expenses/budgets', cleanQuery(query));
  },
  createBudget(body: ExpenseBudgetCreateRequest): Promise<ExpenseBudget> {
    return api.post<ExpenseBudget>('/expenses/budgets', body);
  },
  updateBudget(
    id: string,
    body: { amountCents?: number; remark?: string | null; status?: string },
  ): Promise<ExpenseBudget> {
    return api.patch<ExpenseBudget>(`/expenses/budgets/${id}`, body);
  },
  removeBudget(id: string): Promise<{ ok: boolean }> {
    return api.delete<{ ok: boolean }>(`/expenses/budgets/${id}`);
  },
  createBudgetAdjust(id: string, body: ExpenseBudgetAdjustRequest): Promise<{ id: string }> {
    return api.post<{ id: string }>(`/expenses/budgets/${id}/adjust`, body);
  },
  /** 某条预算的调整记录。批准要按调整单 id 逐条处理（见后端路由的说明） */
  budgetAdjusts(id: string): Promise<ExpenseBudgetAdjust[]> {
    return api.get<ExpenseBudgetAdjust[]>(`/expenses/budgets/${id}/adjusts`);
  },
  decideBudgetAdjust(
    id: string,
    adjustId: string,
    body: { approved: boolean; note?: string | null },
  ): Promise<ExpenseBudget> {
    return api.patch<ExpenseBudget>(`/expenses/budgets/${id}/adjust/${adjustId}`, body);
  },
  budgetExecution(query: ExpenseReportQuery = {}): Promise<ExpenseBudgetExecution[]> {
    return api.get<ExpenseBudgetExecution[]>('/expenses/budgets/execution', cleanQuery(query));
  },

  /* —— 报表（六个维度共用一条聚合，只有路径不同） —— */
  report(
    dimension: 'by-person' | 'by-dept' | 'by-type' | 'by-month' | 'by-project' | 'budget',
    query: ExpenseReportQuery = {},
  ): Promise<ExpenseReportResponse & { dimensionLabel: string }> {
    return api.get(`/expenses/reports/${dimension}`, cleanQuery(query));
  },

  /* —— 预警 —— */
  alerts(query: BizAlertListQuery = {}): Promise<PageResult<BizAlert>> {
    return api.get<PageResult<BizAlert>>('/expenses/alerts', cleanQuery(query));
  },
  handleAlert(id: string, body: BizAlertHandleRequest): Promise<BizAlert> {
    return api.patch<BizAlert>(`/expenses/alerts/${id}`, body);
  },
};

export const archiveApi = {
  list(query: PageQuery & Record<string, unknown>): Promise<PageResult<ArchiveListItem>> {
    return api.get<PageResult<ArchiveListItem>>('/archives', cleanQuery(query));
  },
  /** 导出（审计留痕） */
  export(query: PageQuery & Record<string, unknown>): Promise<{ url?: string; taskId?: string }> {
    return api.post<{ url?: string; taskId?: string }>('/archives/export', cleanQuery(query));
  },
};

/* ==========================================================================
 * 消息
 * ========================================================================== */

export const messageApi = {
  list(query: MessageListQuery): Promise<PageResult<Message>> {
    return api.get<PageResult<Message>>('/messages', cleanQuery(query));
  },
  unreadCount(): Promise<UnreadCountResponse> {
    return api.get<UnreadCountResponse>('/messages/unread-count', undefined, { silent: true });
  },
  markRead(id: string): Promise<void> {
    // 同 updateRole：后端注册的是 PATCH，POST 会 404。
    // 注：本方法目前**前端没有任何调用点**，属预留——但动词先改对，
    // 免得将来接上时又踩一次"404 被当成接口未就绪"的静默降级。
    return api.patch<void>(`/messages/${id}/read`);
  },
};

/* ==========================================================================
 * 租户配置与外部接口
 * ========================================================================== */

export const tenantApi = {
  config(): Promise<TenantConfig> {
    return api.get<TenantConfig>('/tenant/config');
  },
  updateConfig(body: TenantConfigUpdateRequest): Promise<TenantConfig> {
    return api.put<TenantConfig>('/tenant/config', body);
  },
  providers(): Promise<ProviderConfig[]> {
    return api.get<ProviderConfig[]>('/tenant/providers');
  },
  updateProvider(provider: string, body: ProviderConfigUpdateRequest): Promise<ProviderConfig> {
    return api.put<ProviderConfig>(`/tenant/providers/${provider}`, body);
  },
};

export const logApi = {
  operations(query: OperationLogQuery): Promise<PageResult<OperationLog>> {
    return api.get<PageResult<OperationLog>>('/logs/operations', cleanQuery(query));
  },
  providerCalls(query: ProviderCallLogQuery): Promise<PageResult<ProviderCallLog>> {
    return api.get<PageResult<ProviderCallLog>>('/logs/providers', cleanQuery(query));
  },
};

/* ==========================================================================
 * 平台运营端
 * ========================================================================== */

export const platformApi = {
  tenants(query: TenantListQuery): Promise<PageResult<TenantListItem>> {
    return api.get<PageResult<TenantListItem>>('/platform/tenants', cleanQuery(query));
  },
  tenant(id: string): Promise<Tenant> {
    return api.get<Tenant>(`/platform/tenants/${id}`);
  },
  verifyTenant(id: string, body: TenantVerifyRequest): Promise<Tenant> {
    return api.post<Tenant>(`/platform/tenants/${id}/verify`, body);
  },
  suspendTenant(id: string, body: TenantSuspendRequest): Promise<Tenant> {
    return api.post<Tenant>(`/platform/tenants/${id}/suspend`, body);
  },
  changePlan(id: string, body: TenantChangePlanRequest): Promise<Tenant> {
    return api.post<Tenant>(`/platform/tenants/${id}/change-plan`, body);
  },
  adjustQuota(id: string, body: QuotaAdjustRequest): Promise<void> {
    return api.post<void>(`/platform/tenants/${id}/quota`, body);
  },
  /**
   * 重置某租户管理员的密码。
   *
   * 不传 newPassword 则由系统生成一个并返回（只在响应里出现一次）。
   * 这是企业忘记密码时的唯一出路——它和「审核认证」是两件事：
   * 审核是状态迁移（待认证 → 正常，一个租户只能做一次），
   * 重置密码是可反复执行的日常运维，不改变租户状态。
   */
  resetAdminPassword(
    id: string,
    body: { newPassword?: string },
  ): Promise<{
    tenantId: string;
    adminUsername: string;
    newPassword: string;
    generated: boolean;
  }> {
    return api.post(`/platform/tenants/${id}/reset-admin-password`, body);
  },
  plans(): Promise<Plan[]> {
    return api.get<Plan[]>('/platform/plans');
  },
  metrics(): Promise<PlatformMetrics> {
    return api.get<PlatformMetrics>('/platform/metrics');
  },
  auditLogs(query: AuditLogQuery): Promise<PageResult<Record<string, unknown>>> {
    return api.get<PageResult<Record<string, unknown>>>('/platform/audit-logs', cleanQuery(query));
  },
  tplusSync(id: string): Promise<TplusSync> {
    return api.get<TplusSync>(`/platform/tplus-syncs/${id}`);
  },
};

/* ==========================================================================
 * 类型再导出，方便页面只 import 一个模块
 * ========================================================================== */

export type {
  ArchiveListItem,
  AuthContext,
  CheckReport,
  Contract,
  Dept,
  FlowInstance,
  FlowTask,
  FlowTemplate,
  InvoiceDetail,
  InvoiceListItem,
  LoginResponse,
  Message,
  MetadataResponse,
  OperationLog,
  PageResult,
  PlatformMetrics,
  Plan,
  ProviderCallLog,
  ProviderConfig,
  Role,
  Tenant,
  TenantConfig,
  TenantListItem,
  TenantMetrics,
  TplusSync,
  User,
};
